// src/routes/merchants/catalogue/useCatalogue.ts
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { api } from '../../../api/client'
import { keys } from '../../../api/keys'
import { emit } from '../../../platform/analytics/analytics'
import { useEntitlement } from '../../../platform/access/useEntitlement'
import { MerchantsError } from '../useMerchants'
import { toPickerMerchant, type PickerMerchant } from '../picker/merchantPickerModel'
import { brandKey, hideOwned, serverTrim, toBrand, toBrandMatch, type Brand } from './catalogueModel'

/** The picker's Brands section: eight under the user's own merchants (plan 2026-10-16, Task 1). */
export const BRAND_LIMIT = 8
/**
 * The server's bounds on `q` after ITS trimming (`merchant_catalogue.py`, `_Q_MIN`/`_Q_MAX`), counted as it
 * counts, in code points: outside them is a 422, so it is never sent.
 */
export const BRAND_SEARCH_MIN = 2
export const BRAND_SEARCH_MAX = 100
/** `/match` takes 1–200 code points after trimming; a longer bank name is cut rather than refused. */
export const MATCH_NAME_MAX = 200
/** The 409 lookup's page, the list endpoint's largest, and how many it will read before giving up. */
const OWN_PAGE_SIZE = 500
const OWN_PAGES_MAX = 10

const codePoints = (s: string): number => [...s].length

export type BrandSearch = { status: 'idle' | 'loading' | 'error' | 'success'; brands: readonly Brand[] }

const NONE: readonly Brand[] = []

/**
 * `GET /merchant-catalogue/search?q&limit=8` — brands in both countries, the user's and their household's
 * own merchants already hidden by the server. The caller debounces `term` (the picker's search debounce).
 *
 * A section of the picker, not a surface of its own: a failure is `error` with no brands, and the
 * picker simply shows none — as "Most used" does.
 */
export function useBrandSearch(term: string, { enabled = true }: { enabled?: boolean } = {}): BrandSearch {
  const q = serverTrim(term)
  const on = enabled && codePoints(q) >= BRAND_SEARCH_MIN && codePoints(q) <= BRAND_SEARCH_MAX
  const query = useQuery({
    queryKey: keys.merchants.brandSearch(q),
    queryFn: async ({ signal }): Promise<Brand[]> => {
      const { data, error, response } = await api.GET('/api/v1/merchant-catalogue/search', {
        params: { query: { q, limit: BRAND_LIMIT } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data.results.map(toBrand)
    },
    enabled: on,
    retry: false,
  })
  const status: BrandSearch['status'] = !on ? 'idle' : query.data !== undefined ? 'success' : query.isError ? 'error' : 'loading'
  const brands = on ? (query.data ?? NONE) : NONE
  return useMemo(() => ({ status, brands }), [status, brands])
}

/** The name `/match` is asked about: trimmed as the server trims, cut to its 200 code points, trimmed again. */
export const matchName = (name: string | null): string => serverTrim([...serverTrim(name ?? '')].slice(0, MATCH_NAME_MAX).join(''))

/**
 * "Looks like Tim Hortons: add as a merchant?" (#494) — `GET /merchant-catalogue/match?name=`, which
 * answers the one brand a transaction's name looks like, or `null`. The server applies the threshold,
 * drops generic words and hides brands the user (or their household) already has.
 *
 * **A brand a listed merchant already carries is not offered either**, as the picker's Brands do
 * (`hideOwned`): the server's hiding does not consider the shared catalogue's global merchants, so a
 * name matching a global "Tim Hortons" would otherwise offer a personal duplicate of it. That is asked
 * as the picker asks, a merchant search for the brand's name; until it answers nothing is offered, and if
 * it fails the brand is offered, as the picker offers it when its list fails.
 *
 * `names` are transactions' cleaned merchant names; the answer is one brand or `null` per name, in order.
 * A failure is no suggestion: it is an offer, and an offer that cannot be made is simply absent. Under
 * `custom_merchants`, checked here: without it nothing is asked.
 */
export function useBrandMatches(names: readonly string[]): (Brand | null)[] {
  const entitled = useEntitlement('custom_merchants')
  const cleaned = names.map(matchName)
  const matches = useQueries({
    queries: cleaned.map((name) => ({
      queryKey: keys.merchants.brandMatch(name),
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<Brand | null> => {
        const { data, error, response } = await api.GET('/api/v1/merchant-catalogue/match', {
          params: { query: { name } },
          signal,
        })
        if (error !== undefined) throw new MerchantsError(error, response.status)
        // A bare `null` body is the server's "no match"; openapi-fetch hands it over as `null`.
        return toBrandMatch(data ?? null)
      },
      enabled: entitled && name !== '',
      retry: false,
    })),
  })
  const brands = matches.map((m, i) => (entitled && cleaned[i] !== '' ? (m.data ?? null) : null))
  const listed = useQueries({
    queries: brands.map((b) => ({
      queryKey: keys.merchants.brandListed(b?.name ?? ''),
      queryFn: ({ signal }: { signal: AbortSignal }) => isListed(b?.name ?? '', signal),
      enabled: b !== null,
      retry: false,
    })),
  })
  return brands.map((b, i) => {
    const seen = listed[i]
    if (b === null || seen === undefined) return null
    if (seen.isError) return b
    return seen.data === false ? b : null
  })
}

/** One name's suggestion: `useBrandMatches` for a single transaction. */
export function useBrandSuggestion(name: string | null): Brand | null {
  return useBrandMatches([name ?? ''])[0] ?? null
}

/** Is there a merchant the user can see, a global one included, carrying `name`? The picker's test, by `brandKey`. */
async function isListed(name: string, signal: AbortSignal): Promise<boolean> {
  const { data, error, response } = await api.GET('/api/v1/merchants', {
    params: { query: { search: name, page: 1, page_size: 100, include_global: true } },
    signal,
  })
  if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
  return hideOwned([{ id: '', name, region: '', suggestedCategory: null }], data.merchants.map((m) => m.name)).length === 0
}

/**
 * Items (the No merchant page's name groups) under the brand each one looks like, **each brand once**:
 * "TIM HORTONS #0412" and "Tim Hortons 88" are two groups and one suggestion. In the order each brand is
 * first matched; items with no brand are left out.
 */
export function useBrandGroups<T>(items: readonly T[], nameOf: (item: T) => string): { brand: Brand; name: string; items: T[] }[] {
  const brands = useBrandMatches(items.map(nameOf))
  const out = new Map<string, { brand: Brand; name: string; items: T[] }>()
  items.forEach((item, i) => {
    const brand = brands[i]
    if (brand === null || brand === undefined) return
    const at = out.get(brand.id)
    if (at === undefined) out.set(brand.id, { brand, name: nameOf(item), items: [item] })
    else at.items.push(item)
  })
  return [...out.values()]
}

export type AddBrandResult =
  | {
      ok: true
      merchant: PickerMerchant
      /** A 409: the user already had a merchant by this name, and this is it. Nothing was created. */
      existing: boolean
      suggestedCategory: { id: string; name: string } | null
    }
  | { ok: false; status: number | null }
export type AddBrand = (brand: Brand, opts: { householdId: string | null }) => Promise<AddBrandResult>

/**
 * `POST /merchant-catalogue/{id}/add {household_id?}` → `{merchant, suggested_category}`. The merchant is
 * the user's own; with a household it is also shared with it. Resolves, never rejects: a failure carries
 * its HTTP status, as the picker's create does.
 *
 * **A 409 means a merchant with this name already exists** (the server compares canonical names in the
 * keyspace it writes to, and never returns the row). The existing merchant is found by that same
 * canonical name (`brandKey`) and handed back as `existing`, so the caller assigns it instead: no
 * duplicate is ever created. If it cannot be found, the 409 stands.
 *
 * Every merchant read is invalidated on success and on a 409, the brand reads with them (they sit under
 * the same root), so the brand stops being offered. Analytics: `merchant_created`, bare, as a typed
 * create sends it; never the brand's name or id (rule 7).
 */
export function useAddBrand(): AddBrand {
  const client = useQueryClient()
  return useCallback<AddBrand>(
    async (brand, { householdId }) => {
      try {
        const { data, error, response } = await api.POST('/api/v1/merchant-catalogue/{catalogue_id}/add', {
          params: { path: { catalogue_id: brand.id } },
          body: householdId === null ? {} : { household_id: householdId },
        })
        if (error !== undefined || data === undefined) {
          if (response.status !== 409) return { ok: false, status: response.status }
          void client.invalidateQueries({ queryKey: keys.merchants.all() })
          const found = await findOwnByKey(brand.name).catch(() => undefined)
          return found === undefined ? { ok: false, status: 409 } : { ok: true, merchant: found, existing: true, suggestedCategory: brand.suggestedCategory }
        }
        void client.invalidateQueries({ queryKey: keys.merchants.all() })
        emit('merchant_created')
        const category = data.suggested_category
        return {
          ok: true,
          merchant: toPickerMerchant(data.merchant),
          existing: false,
          suggestedCategory: category == null ? brand.suggestedCategory : { id: category.id, name: category.name },
        }
      } catch {
        return { ok: false, status: null }
      }
    },
    [client],
  )
}

/**
 * The user's own (or household's) active merchant whose canonical name is `name`'s — the one a 409 names.
 *
 * Their own merchants are paged A–Z with NO search and matched here by `brandKey`. The list's `search` is a
 * substring match on the raw name, so it cannot find "A & W" for "A&W" or "Tim Horton's" for "Tim
 * Hortons", which the server's key calls the same name.
 */
async function findOwnByKey(name: string): Promise<PickerMerchant | undefined> {
  const key = brandKey(name)
  for (let page = 1; page <= OWN_PAGES_MAX; page++) {
    const { data, error, response } = await api.GET('/api/v1/merchants', {
      params: { query: { page, page_size: OWN_PAGE_SIZE, include_global: false, sort: 'name' } },
    })
    if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
    const hit = data.merchants.find((m) => !m.is_global && m.is_active && brandKey(m.name) === key)
    if (hit !== undefined) return toPickerMerchant(hit)
    if (!data.has_more) return undefined
  }
  return undefined
}
